import React, { useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import './TradingInterface.css';

const TradingInterface: React.FC = () => {
  const { state, dispatch } = usePortfolio();
  const { stocks, portfolio } = state;
  
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('buy');
  const [quantity, setQuantity] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedStockData = stocks.find(stock => stock.ticker === selectedStock);
  const selectedHolding = portfolio.holdings.find(holding => holding.ticker === selectedStock);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedStockData || !quantity || parseInt(quantity) <= 0) {
      return;
    }

    const qty = parseInt(quantity);
    const price = selectedStockData.currentPrice;
    const total = qty * price;

    // Validation
    if (orderType === 'buy' && portfolio.cash < total) {
      alert('Insufficient funds');
      return;
    }

    if (orderType === 'sell' && (!selectedHolding || selectedHolding.quantity < qty)) {
      alert('Insufficient shares');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      dispatch({
        type: orderType === 'buy' ? 'BUY_STOCK' : 'SELL_STOCK',
        payload: {
          ticker: selectedStock,
          quantity: qty,
          price: price
        }
      });

      // Reset form
      setQuantity('');
      alert(`${orderType === 'buy' ? 'Buy' : 'Sell'} order executed successfully!`);
    } catch (error) {
      alert('Order failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMaxQuantity = () => {
    if (!selectedStockData) return 0;
    
    if (orderType === 'buy') {
      return Math.floor(portfolio.cash / selectedStockData.currentPrice);
    } else {
      return selectedHolding?.quantity || 0;
    }
  };

  const handleQuantityChange = (value: string) => {
    const num = parseInt(value);
    const max = getMaxQuantity();
    
    if (value === '' || (num > 0 && num <= max)) {
      setQuantity(value);
    }
  };

  const setMaxQuantity = () => {
    setQuantity(getMaxQuantity().toString());
  };

  const totalCost = selectedStockData && quantity ? 
    parseInt(quantity) * selectedStockData.currentPrice : 0;

  return (
    <div className="trading-interface">
      <div className="trading-header">
        <h3>Place Order</h3>
        <span className="trading-subtitle">Market orders only</span>
      </div>

      <form onSubmit={handleSubmit} className="trading-form">
        <div className="form-group">
          <label htmlFor="stock-select">Select Stock</label>
          <select
            id="stock-select"
            value={selectedStock}
            onChange={(e) => {
              setSelectedStock(e.target.value);
              setQuantity('');
            }}
            className="stock-select"
            required
          >
            <option value="">Choose a stock...</option>
            {stocks.map(stock => (
              <option key={stock.ticker} value={stock.ticker}>
                {stock.ticker} - {stock.name}
              </option>
            ))}
          </select>
        </div>

        {selectedStockData && (
          <div className="stock-info">
            <div className="stock-price-info">
              <span className="current-price">
                {formatCurrency(selectedStockData.currentPrice)}
              </span>
              <span className={`price-change ${selectedStockData.change >= 0 ? 'positive' : 'negative'}`}>
                {selectedStockData.change >= 0 ? '+' : ''}{formatCurrency(selectedStockData.change)} 
                ({selectedStockData.changePercent >= 0 ? '+' : ''}{selectedStockData.changePercent.toFixed(2)}%)
              </span>
            </div>
            {selectedHolding && (
              <div className="holding-info">
                <span>You own {selectedHolding.quantity} shares</span>
                <span>Avg: {formatCurrency(selectedHolding.averagePrice)}</span>
              </div>
            )}
          </div>
        )}

        <div className="order-type-group">
          <label>Order Type</label>
          <div className="order-type-buttons">
            <button
              type="button"
              className={`order-type-btn ${orderType === 'buy' ? 'active' : ''}`}
              onClick={() => {
                setOrderType('buy');
                setQuantity('');
              }}
            >
              Buy
            </button>
            <button
              type="button"
              className={`order-type-btn ${orderType === 'sell' ? 'active' : ''}`}
              onClick={() => {
                setOrderType('sell');
                setQuantity('');
              }}
              disabled={!selectedHolding}
            >
              Sell
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="quantity">Quantity</label>
          <div className="quantity-input-group">
            <input
              id="quantity"
              type="number"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              placeholder="0"
              min="1"
              max={getMaxQuantity()}
              className="quantity-input"
              required
            />
            <button
              type="button"
              className="max-btn"
              onClick={setMaxQuantity}
              disabled={getMaxQuantity() === 0}
            >
              Max
            </button>
          </div>
          <div className="quantity-info">
            <span>Max: {getMaxQuantity()} shares</span>
            {orderType === 'buy' && (
              <span>Available: {formatCurrency(portfolio.cash)}</span>
            )}
          </div>
        </div>

        {quantity && selectedStockData && (
          <div className="order-summary">
            <div className="summary-row">
              <span>Quantity:</span>
              <span>{quantity} shares</span>
            </div>
            <div className="summary-row">
              <span>Price per share:</span>
              <span>{formatCurrency(selectedStockData.currentPrice)}</span>
            </div>
            <div className="summary-row total">
              <span>Total {orderType === 'buy' ? 'Cost' : 'Proceeds'}:</span>
              <span>{formatCurrency(totalCost)}</span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className={`submit-btn ${orderType === 'buy' ? 'buy' : 'sell'}`}
          disabled={!selectedStock || !quantity || isSubmitting || getMaxQuantity() === 0}
        >
          {isSubmitting ? 'Processing...' : `${orderType === 'buy' ? 'Buy' : 'Sell'} ${quantity || 0} Shares`}
        </button>
      </form>
    </div>
  );
};

export default TradingInterface;
